import Decimal from 'decimal.js-light';

import { formatLimitRate } from '@/components/TransactionModule/LimitMode/utils';

import {
  DAI_ADDRESSES,
  MAIN_USD_STABLE_ADDRESSES,
  SOLANA_STABLECOINS,
  USDC_ADDRESSES,
  USDT_ADDRESSES,
} from '@/constants/addresses';
import { isAdvancedOrderTypeSupported } from '@/constants/advancedOrder';
import { CHAIN_IDS } from '@/constants/chain';

import type { ChainAddress } from '@/utils/models';
import { isTokenAddressNativeAsset, isUsdStableAddress } from '@/utils/multichain';
import { normalizeTokenAddress } from '@/utils/normalizeAddress';

export type TriggerOrderUiLocation = 'portfolio' | 'post_swap';

export function isTriggerOrderEligibleToken(address: ChainAddress, chainId: number): boolean {
  return (
    isAdvancedOrderTypeSupported(chainId, 'stop-loss') &&
    !isUsdStableAddress(address, chainId) &&
    !isTokenAddressNativeAsset(address, chainId) &&
    getPostSwapOutputCandidates(address, chainId).length > 0
  );
}

export function getPostSwapOutputCandidates(
  buyAddress: ChainAddress,
  chainId: number,
): ChainAddress[] {
  const seen = new Set([normalizeTokenAddress(buyAddress, chainId)]);
  const candidates: ChainAddress[] = [];
  const stablecoinAddresses = [
    MAIN_USD_STABLE_ADDRESSES[chainId],
    USDC_ADDRESSES[chainId],
    USDT_ADDRESSES[chainId],
    DAI_ADDRESSES[chainId],
    ...(chainId === CHAIN_IDS.SOLANA ? SOLANA_STABLECOINS.map(({ address }) => address) : []),
  ];

  for (const address of stablecoinAddresses) {
    if (!address) continue;
    const key = normalizeTokenAddress(address, chainId);
    if (seen.has(key)) continue;
    seen.add(key);
    candidates.push(address);
    if (candidates.length === 3) break;
  }

  return candidates;
}

export function getTriggerOrderAmounts({
  buyTokenDecimals,
  sellAmount,
  triggerRate,
}: {
  buyTokenDecimals: number;
  sellAmount: string;
  triggerRate: string;
}): { buyAmount: string; triggerRate: string } {
  const formattedTriggerRate = new Decimal(triggerRate).toString();
  const buyAmount = new Decimal(sellAmount)
    .mul(formattedTriggerRate)
    .toDecimalPlaces(buyTokenDecimals, Decimal.ROUND_DOWN)
    .toString();

  return {
    triggerRate: formattedTriggerRate,
    buyAmount,
  };
}

export function getTriggerRateFromPercentage(marketRate: string, percentage: number): string {
  return formatLimitRate(
    new Decimal(marketRate).mul(new Decimal(1).add(new Decimal(percentage).div(100))).toString(),
  );
}

export function getTriggerPercentage(marketRate: string, triggerRate: string): number {
  return new Decimal(triggerRate).div(marketRate).sub(1).mul(100).toNumber();
}
